import React from 'react';
import PropTypes from 'prop-types';
import Immutable from 'seamless-immutable';
import NavActions from '../../actions';
import SiteNavKids from './SiteNavKids';
import {hrefIsActive, getCurrentUrl} from '../../utils/urlUtils';
import {getDataAttribute} from 'epic-common/src/browser';
import debounce from 'lodash.debounce';
import {getSiteNavMobileHeight} from '../../utils/resizeUtils';
import root from 'window-or-global';

export default class SiteNav extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            activeParentLink: null, // the index of the active link
            activeChildLink: null, // the index of the active child link
            siteNavHeight: '100vh' //height the #siteNav should be
        };

        this.currentLinkArray = [];

        this.resizeListenerOn = false;
        this.dead = false;

        this.resizeDebounce = debounce(() => {
            this.setHeight(this.props, this.context);
        }, 100);

        this.cache = {};
    }

    static propTypes = {
        links: PropTypes.array,
        showLinks: PropTypes.bool,
        mobileCollapsed: PropTypes.bool,
        mobileActiveLinkIndex: PropTypes.number,
        searchActive: PropTypes.bool
    };

    static contextTypes = {
        isMobile: PropTypes.bool,
        isTablet: PropTypes.bool,
        isDesktop: PropTypes.bool,
        collapsedDesktop: PropTypes.bool,
        globalLinkOnClick: PropTypes.func
    };

    componentWillReceiveProps(nextProps, nextContext) {
        this.checkActiveLink(nextProps);
        this.setHeight(nextProps, nextContext);

        if (nextProps.showLinks && nextContext.isMobile && !this.resizeListenerOn) {
            this.resizeListenerOn = true;
            root.addEventListener('resize', this.resizeDebounce);
        }

        if (this.resizeListenerOn && (!nextProps.showLinks || !nextContext.isMobile)) {
            root.removeEventListener('resize', this.resizeDebounce);
            this.resizeListenerOn = false;
        }
    }

    componentWillMount() {
        this.checkActiveLink();
    }

    componentWillUnmount() {
        this.dead = true;
        root.removeEventListener('resize', this.resizeDebounce);
    }

    componentDidUpdate() {
        if (this.props.mobileCollapsed) {
            this.resizeDebounce();
        }
    }

    setHeight(props, context) {
        if (this.dead) return;
        //set the height for #siteNav when in mobile based off of the icon-bar
        let siteNavHeight = '3.7em';
        if (context.isMobile) {
            if (props.showLinks) {
                siteNavHeight = getSiteNavMobileHeight();
            } else if (!props.showLinks) {
                siteNavHeight = 0;
            }
        } else if (context.isTablet && props.showLinks) {
            siteNavHeight = '100vh'; //tablet
        } else if (context.isTablet && !props.showLinks) {
            siteNavHeight = 0;
        }

        if (this.state.siteNavHeight !== siteNavHeight) {
            //console.debug('siteNavHeight', siteNavHeight);
            this.setState({siteNavHeight});
        }
    }

    /**
     * Checks the link parameter if it exists to see if it is active otherwise looks through the props.links
     * to try and determine which link is active.
     *
     * The first check is to see if a flag has been used on the link to indicate active, then will match against the
     * current URL.
     */
    checkActiveLink = (props) => {
        if (this.dead) return;
        let linkArray = [];
        //componentWillReceiveProps may hand over newer props than this.props
        if (props && props.links && props.links.asMutable) {
            linkArray = props.links.asMutable({deep: true});
            if (this.cache.links === props.links && this.cache.url === getCurrentUrl()) {
                return; //executing the rest of this function will result in no changes.
            }
            this.cache.links = props.links;
            this.cache.url = getCurrentUrl();
        } else if (this.props && this.props.links && this.props.links.asMutable) {
            linkArray = this.props.links.asMutable({deep: true});
            if (this.cache.links === this.props.links && this.cache.url === getCurrentUrl()) {
                return; //executing the rest of this function will result in no changes.
            }
            this.cache.links = this.props.links;
            this.cache.url = getCurrentUrl();
        } else {
            this.setState({
                activeParentLink: null,
                activeChildLink: null
            });
            return;
        }

        let foundActiveParent = false;

        //look to see if any of the parents and their kids have the boolean flag 'active' to true and use this
        linkArray.forEach((parentLink, i) => {
            //check kid links first. if kid is active parent must be
            if (parentLink.kids) {
                const kidArray = parentLink.kids.asMutable ? parentLink.kids.asMutable() : parentLink.kids || [];
                if (kidArray) {
                    kidArray.forEach((kidLink, j) => {
                        if (kidLink.active || hrefIsActive(kidLink.href)) {
                            foundActiveParent = true;
                            this.setState({
                                activeParentLink: i,
                                activeChildLink: j
                            });
                            return false;
                        }
                        return undefined;
                    });
                }
            }

            //check parent level links if no kids are active
            if (!foundActiveParent && (parentLink.active || hrefIsActive(parentLink.href))) {
                foundActiveParent = true;

                this.setState({
                    activeParentLink: i
                });

                return false;
            }
            return undefined;
        });

        //reset the active to nothing
        if (!foundActiveParent) {
            this.setState({
                activeParentLink: null,
                activeChildLink: null
            });
        }
    };

    closeMobileTablet = () => {
        NavActions.closeMobileTablet.defer();
    };

    clickLink = (e) => {
        if (e.button && e.button > 0) return;
        //skip onClick for collapseParents
        const collapseParent = getDataAttribute(e.currentTarget, 'collapseparent');
        if (collapseParent === true || collapseParent === 'true') {
            return;
        }

        const index = getDataAttribute(e.currentTarget, 'index');
        let link;
        if (this.context.isDesktop) {
            link = this.currentLinkArray[index];
        } else {
            link = this.props.links[index];
        }

        const href = link.href;
        let globalReturnValue = null;
        if (this.context.globalLinkOnClick && typeof this.context.globalLinkOnClick === 'function') {
            globalReturnValue = this.context.globalLinkOnClick(e, href);
        }

        const hasKids = this.hasKids(link);
        if ((this.context.isMobile || this.context.isTablet) && hasKids) {
            NavActions.mobileActiveLinkIndex(+index);
            e.preventDefault();
        }

        if (globalReturnValue !== false) {
            if (link && link.onClick && typeof link.onClick === 'function') {
                link.onClick(e);
            }
        }

        //allow the callback call stack to clear before checking
        setTimeout(() => {
            this.checkActiveLink();
        }, 100);

        if (!this.hasKids(link)) {
            NavActions.closeMobileTablet.defer();
        }
    };

    collapseLinks(linkArray) {
        const tabletArray = [];
        const collapseMap = {
            //collapseTarget: []
            //Games: [{ title: 'foo', collapseTarget: 'Games'}, { title: 'bar', collapseTarget: 'Games'}]
        };

        if (linkArray) {
            //look for collapse targets. add parent of the collapse target and add to map to add later
            linkArray.forEach((link) => {
                if (!link.collapseTarget) {
                    tabletArray.push(link);
                } else {
                    link.isCollapsed = true;
                    if (this.hasKids(link)) {
                        link.hasKids = true;
                    }
                    if (!collapseMap[link.collapseTarget]) {
                        tabletArray.push({title: link.collapseTarget, collapseParent: true});
                        collapseMap[link.collapseTarget] = [link];
                    } else {
                        collapseMap[link.collapseTarget].push(link);
                    }
                }
            });
        }

        //add the children back into the array from the map as kids
        tabletArray.forEach((link) => {
            const collapsedArray = collapseMap[link.title];

            if (collapsedArray && collapsedArray.length) {
                link.kids = collapsedArray;
            }
        });

        return Immutable(tabletArray);
    }

    hasKids(link) {
        if (link.kids && link.kids.length && link.kids[0].title) {
            return true;
        }
        return false;
    }

    getSiteNavLinks(linkArray) {
        const mobileKidPagesArray = [];

        const links = linkArray.map((link, i) => {
            if (link.title && !link.isBarDivider) {
                const hasKids = this.hasKids(link);
                let linkClass = '';
                const activeChild = this.state.activeParentLink === i ? this.state.activeChildLink : null;

                if (hasKids && (this.context.isTablet || this.context.isMobile)) {
                    linkClass = 'sub-link-item back-arrow-before-after';
                    mobileKidPagesArray.push({
                        parentIndex: i,
                        parentLink: link,
                        activeChild
                    });
                } else if (hasKids && (!this.context.isMobile && !this.context.isTablet)) {
                    linkClass = 'dropdown';
                }

                if (this.state.activeParentLink !== null && this.state.activeParentLink === i) {
                    linkClass += ' active text-color-active';
                }

                let linkContent;
                if (this.context.isTablet || (this.context.isMobile && !link.imgSrc)) {
                    linkContent = <span className="text-color">{link.title}</span>;
                } else if (link.imgSrc) {
                    linkContent = (
                        <img
                            alt="sub header"
                            className="sitenav-img"
                            id="egSubHeaderImg"
                            src={link.imgSrc} />
                    );
                } else {
                    linkContent = <p>{link.title}</p>;
                }

                const collapseParent = link.collapseParent ? '-collapseParent' : '';

                return (
                    <li
                        role="menuitem"
                        key={`sitenav-link-${link.title}${collapseParent}`}
                        id={`sitenav-link-${i}${collapseParent}`}
                        className={`${linkClass} ${!this.context.isMobile ? 'accent-bg-color-before' : 'mobile-list-item'}
                            ${this.context.isTablet ? 'mobile-list-item' : ''}`}
                        onClick={this.clickLink}
                        data-collapseparent={link.collapseParent}
                        data-index={i}>
                        <a
                            href={link.href || (hasKids ? null : '#')}
                            className={`text-color-nonactive text-color-hover ${!link.href ? 'cursor-default' : ''}`}
                            target={link.target}>
                            {linkContent}
                        </a>
                        {hasKids ?
                            <SiteNavKids
                                kids={Immutable(link.kids)}
                                activeChild={activeChild}
                                parentIndex={i} /> : ''}
                    </li>
                );
            } else if (link.isBarDivider && this.context.isDesktop && !this.context.collapsedDesktop) {
                return (
                    <li
                        key={`sitenav-link-${link.title}`}
                        id={`sitenav-link-${i}`}
                        className="link-bar-divider text-color-before-after" />
                );
            }
            if (!link.isBarDivider) {
                if (process.env.NODE_ENV !== 'production') {
                    console.warn('Site Link was not passed a title!');
                }
            }
            return undefined;
        });

        const mobileKidPages = this.getMobileKidPages(mobileKidPagesArray);

        return {links, mobileKidPages};
    }

    getMobileKidPages(mobileKidPagesArray) {
        return mobileKidPagesArray.map((kidObject) => {
            let active = false;
            if (kidObject.parentIndex === this.props.mobileActiveLinkIndex) {
                active = true;
            }

            return (
                <SiteNavKids
                    parentLink={kidObject.parentLink}
                    key={`sitenav-kids-${kidObject.parentIndex}`}
                    active={active}
                    parentIndex={kidObject.parentIndex}
                    kids={Immutable(kidObject.parentLink.kids)}
                    isMobile={this.context.isMobile}
                    isTablet={this.context.isTablet}
                    activeChild={kidObject.activeChild || null} />
            );
        });
    }

    render() {
        let linkArray = this.props.links || [];
        if (this.props.links && this.props.links.asMutable) {
            linkArray = this.props.links.asMutable({deep: true});
        } else {
            console.warn('[EGH] SiteNav Expects a seamless immutable array!!');
        }

        const seachActive = this.props.searchActive || false;
        const showLinks = this.props.showLinks || false;

        if (this.context.isDesktop && this.context.collapsedDesktop) {
            linkArray = this.collapseLinks(linkArray);
        }

        const siteNavLinks = this.getSiteNavLinks(linkArray);

        this.currentLinkArray = linkArray;
        return (
            <div className={`sitenav-wrap bg-color ${showLinks ? 'active' : ''} ${seachActive ? 'search-active' : ''}`}>
                {this.context.isTablet ?
                    <div role="button" tabIndex="0" className={`mobile-overlay ${showLinks ? 'active' : ''}`} onClick={this.closeMobileTablet} />
                    : ''}
                <ul
                    id="siteNav"
                    style={{height: this.state.siteNavHeight}}
                    className={`siteNav bg-color ${this.context.isMobile ? 'mobile mobile-list' : ''} ${this.context.isTablet ? 'tablet' : ''}  ${showLinks ? 'active' : ''}`}>
                    {siteNavLinks.links}
                </ul>
                {siteNavLinks.mobileKidPages}
            </div>
        );
    }
}
